import { Wrench } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { MaintenanceLevel } from '@/api/appCompatibility';

type MaintenanceBannerProps = {
  level: MaintenanceLevel;
  retryAfterSeconds?: number | null;
};

const LEVEL_KEY: Record<MaintenanceLevel, string> = {
  read_only: 'readOnly',
  full: 'full'
};

const LEVEL_CLASS: Record<MaintenanceLevel, string> = {
  read_only: 'bg-signal-warn text-white',
  full: 'bg-signal-danger text-white'
};

/**
 * Non-dismissible maintenance notice (phase C.5). Shown — rather than a
 * full-screen block — for both 423 (read-only) and 503 (full outage), because
 * maintenance is a transient *server* state: the user's local data and read
 * flows stay valuable, and the sync queue is paused separately so writes are not
 * lost. A hard block is reserved for 426 (the client itself is unsupported).
 * Copy is localized per level; the backend's raw `message` is never displayed.
 *
 * Layout (C.6): rendered inside `AppLayout`, directly under its header, and
 * `sticky top-16` so it pins just below that header (which is `sticky top-0`
 * around an `h-16`/64px bar) and stays on screen as the page scrolls. `z-10`
 * matches the header's layer so page content scrolls underneath both. This must
 * be persistent — not scroll-away — because FuelOps is a regulated context: an
 * operator filling a legally-critical log (e.g. a Fuel Reception bitácora) has to
 * know the backend is in maintenance *before* hitting "Guardar", or the write
 * silently parks in the paused sync queue with no on-screen explanation.
 */
export const MaintenanceBanner = ({ level, retryAfterSeconds }: MaintenanceBannerProps) => {
  const { t } = useTranslation();
  const key = LEVEL_KEY[level];

  return (
    <div
      role="status"
      aria-live="polite"
      className={`sticky top-16 z-10 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium ${LEVEL_CLASS[level]}`}
    >
      <Wrench className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span>
        {t(`appVersion.maintenance.${key}.body`)}
        {typeof retryAfterSeconds === 'number' && retryAfterSeconds > 0
          ? ` ${t('appVersion.maintenance.retryHint')}`
          : ''}
      </span>
    </div>
  );
};
